import { useState, useEffect, useCallback, createElement as rc, Children, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { testProperties } from 'lib_ui-primitives';
import { useTheme } from 'styled-components';
import useHiddenForViewPort from '../../hooks/useHiddenForViewPort';
import useIsInActiveRoute from '../../hooks/useIsInActiveRoute';
import useDisabledForNetwork from '../../hooks/useDisabledForNetwork';
import { DIRECTION } from '../_abstractComponent/DropDown/ArrowIcon';

import {
    DisabledMenuButton,
    LinkText,
    MenuButton,
    MenuPlusSubMenus,
    NavIcon,
    NavigationButton,
    NoIcon,
    SubMenu,
    SubMenuChevron,
    SubMenuChildrenArea,
    disabledNavigationButton
} from './styles.gl2025';
import { NavbarContext } from '../appLayout/NavBarContextProvider.gl2025';

/**
 * navHeadings implement (Route) Navigational Support ONLY.
 * In this case, it generates how a NavHeading is shown in the menu (in the sideNav)
 * Any _CONTENT_ other than other NavHeading is passed down into, and generated from, RouteContainer without this "NavHeading" component
 * This includes nav:TabNav and nav:SelectionNav containers
 * @param {*} props
 * @returns
 */
function NavHeading(props) {
    const {
        id: _id,
        children,
        currentRoute,
        hNode: { title, iconName, iconColor },
        hNode
    } = props || { hNode: {} };
    const id = _id || hNode.id;
    const hiddenForViewPort = useHiddenForViewPort(hNode);
    const { disabledByNetwork, hiddenByNetwork, disabledMessage } = useDisabledForNetwork(hNode);
    const isActive = useIsInActiveRoute(props);
    const theme = useTheme();

    const { ready, open: navOpen, setOpen, locked: navLocked, setLocked } = useContext(NavbarContext);

    const navHeadingChildren = useMemo(
        () => Children.toArray(children).filter(child => child.props?.hNode?.hNodeTypeGroup === 'navHeading'),
        [children]
    );
    const getInitialIsCollapsedState = useCallback(() => {
        if (navHeadingChildren.length < 1) return null;
        return !isActive;
    }, [isActive, navHeadingChildren.length]);

    const [isCollapsed, setIsCollapsed] = useState(getInitialIsCollapsedState);

    useEffect(() => {
        setIsCollapsed(getInitialIsCollapsedState);
    }, [isActive, getInitialIsCollapsedState]);

    const toggleIsCollapsed = useCallback(() => {
        setIsCollapsed(prevVal => !prevVal);
    }, []);

    const closeSidebarOnMobile = useCallback(() => {
        if (theme.mobile) {
            setOpen(false);
            setLocked(false);
        }
    }, [setOpen, setLocked, theme.mobile]);

    if (!ready || hiddenForViewPort || hiddenByNetwork) {
        return null;
    }

    const iconOnly = !navLocked && !navOpen;
    if (disabledByNetwork) {
        // Encapsulate to ensure flex-direction is correct.
        // prettier-ignore
        return rc(disabledNavigationButton, null,
                rc(DisabledMenuButton, { id, title: disabledMessage  }, // icon (if any)
                    iconName && rc(NavIcon, { fontColor: theme.outerMenus.disabledFontColor, name:iconName }, iconName),
                    !iconName && rc(NoIcon),
                    rc(LinkText, { disabled: true }, title)
                )
            );
    } else if (navHeadingChildren.length < 1) {
        // it's a menu/link
        const iconFontColor = isActive ? theme.outerMenus.active.fontColor : iconColor;
        // prettier-ignore
        return rc(NavigationButton, { to: currentRoute, onClick: closeSidebarOnMobile },
            rc(MenuButton,{ id, selected: isActive, iconOnly, title },
                // icon (if any)
                iconName && rc(NavIcon, { fontColor: iconFontColor, selected: isActive, name:iconName }, iconName),
                !iconName && rc(NoIcon),
                //title
                rc(LinkText, { hidden: iconOnly, selected: isActive }, title)
                // no collapse indicator
            )
        );
    } else {
        // It's a collapsible menu
        const hideSubMenus = !navOpen || isCollapsed;
        const iconFontColor = isActive && isCollapsed ? theme.outerMenus.active.fontColor : iconColor;
        // prettier-ignore
        return rc(MenuPlusSubMenus, null,
            rc(SubMenu,{ id, selected: isActive, iconOnly, isCollapsed, title, onClick: toggleIsCollapsed, isSubSubMenu: !iconName, 'aria-expanded': !isCollapsed },
                // icon (if any)
                iconName && rc(NavIcon, { fontColor: iconFontColor, selected: isActive && isCollapsed, name:iconName }, iconName),
                !iconName && rc(NoIcon),
                //title
                rc(LinkText, { hidden: iconOnly, selected:isActive && isCollapsed }, title),
                //  collapse indicator
               rc(SubMenuChevron, { 
                    ...testProperties({ id }, 'chevron'), 
                    id: `chevron-${id}`, 
                    hidden: iconOnly, 
                    isOpen: !isCollapsed, 
                    closed: DIRECTION.down, 
                    open: DIRECTION.up,
                    selected: isActive && isCollapsed
                })
            ),
            rc(SubMenuChildrenArea, { isCollapsed: hideSubMenus }, navHeadingChildren)
        );
    }
}

NavHeading.propTypes = {
    id: PropTypes.string,
    currentRoute: PropTypes.string.isRequired,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element]),
    hNode: PropTypes.object,
    hiddenForViewport: PropTypes.bool
};

export default NavHeading;
